import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  canPostToBoard,
  isAdminEmail,
  localizedBoardName,
} from '@/components/forum/forum-i18n'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Pin } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

const TITLE_MAX = 150
const BODY_MAX = 10000

export function ForumNewThreadPage() {
  const { t } = useI18n()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [searchParams] = useSearchParams()

  const { data: boardsData } = useQuery(trpc.forumRouter.listBoards.queryOptions())
  const allBoards = boardsData?.boards ?? []
  const boards = allBoards.filter(b => canPostToBoard(b.slug, user?.email))

  const [board, setBoard] = useState(searchParams.get('board') ?? '')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [isPinned, setIsPinned] = useState(false)

  const requestedBoard = board || searchParams.get('board') || ''
  const isRequestedBoardPostable = boards.some(b => b.slug === requestedBoard)
  const boardSlug = isRequestedBoardPostable
    ? requestedBoard
    : boards[0]?.slug || ''
  const canPinThread = isAdminEmail(user?.email)

  const createThread = useMutation({
    ...trpc.forumRouter.createThread.mutationOptions(),
    onSuccess: ({ id }) => {
      toast.success(t('forum.threadCreated'))
      navigate(`/forum/thread/${id}`)
    },
    onError: () => toast.error(t('forum.errorGeneric')),
  })

  const submit = () => {
    if (!title.trim()) {
      toast.error(t('forum.titleRequired'))
      return
    }
    if (!body.trim()) {
      toast.error(t('forum.bodyRequired'))
      return
    }
    createThread.mutate({
      boardSlug,
      title: title.trim(),
      body: body.trim(),
      ...(canPinThread && isPinned ? { isPinned: true } : {}),
    })
  }

  if (!isAuthenticated) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 pb-12 pt-24 md:px-6">
        <p className="text-muted-foreground">{t('forum.signInRequired')}</p>
        <Button
          className="mt-4"
          onClick={() => navigate(`/signin?redirect=${encodeURIComponent(`/forum/new?board=${boardSlug}`)}`)}
        >
          {t('forum.signIn')}
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-16 pt-24 md:px-6">
      <Link
        to={`/forum?board=${boardSlug}`}
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('forum.backToForum')}
      </Link>

      <h1 className="mb-6 text-2xl font-bold text-foreground">{t('forum.newThread')}</h1>

      <div className="space-y-5">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{t('forum.board')}</label>
          <Select value={boardSlug} onValueChange={setBoard}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {boards.map(b => (
                <SelectItem key={b.slug} value={b.slug}>
                  {localizedBoardName(t, b)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label htmlFor="thread-title" className="text-sm font-medium text-foreground">
            {t('forum.threadTitle')}
          </label>
          <Input
            id="thread-title"
            value={title}
            maxLength={TITLE_MAX}
            placeholder={t('forum.threadTitlePlaceholder')}
            onChange={e => setTitle(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="thread-body" className="text-sm font-medium text-foreground">
            {t('forum.threadBody')}
          </label>
          <Textarea
            id="thread-body"
            value={body}
            maxLength={BODY_MAX}
            rows={8}
            placeholder={t('forum.threadBodyPlaceholder')}
            onChange={e => setBody(e.target.value)}
          />
        </div>

        {canPinThread && (
          <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
            <Checkbox
              id="thread-pinned"
              checked={isPinned}
              onCheckedChange={checked => setIsPinned(checked === true)}
            />
            <label
              htmlFor="thread-pinned"
              className="flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground"
            >
              <Pin className="size-4 text-amber-600 dark:text-amber-300" />
              {t('forum.pinThread')}
            </label>
          </div>
        )}

        <div className="flex gap-3">
          <Button disabled={createThread.isPending || !boardSlug} onClick={submit}>
            {createThread.isPending ? t('forum.creating') : t('forum.createThread')}
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/forum?board=${boardSlug}`)}>
            {t('forum.cancel')}
          </Button>
        </div>
      </div>
    </div>
  )
}
